import { Injectable, inject } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ISoffMapMarker, SoffMapMarkerVehicle, SoffMapModelVehicle } from '@dsm-fe-modules/dsm-fe-map';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnlineStatusEnum } from 'src/app/shared/models/telemetry.model';
import { environment } from '../../../../environments/environment';
import { DefaultViewFilterSto } from '../../../shared/models/default-view-filter.model';
import { DsmApiResponseDataDto } from '../../../shared/models/dsm-api-call.model';
import { DsmApiCall } from '../../../shared/services/dsm-api-call.service';
import { ServiceVanMasterDataDto } from '../models/servicevan.model.dto';
import { ServiceVanCardInfoSto, ServiceVanListTableRow, ServiceVanMasterDataSto } from '../models/servicevan.model.sto';

@Injectable({
  providedIn: 'root',
})
export class ServiceVansService {
  private serviceCall = inject(DsmApiCall);

  loadServiceVanList(viewFilter: DefaultViewFilterSto, sort: Sort | null, offset: number): Observable<DsmApiResponseDataDto> {
    let url: string =
      environment.apiBaseUrl + '/servicevans/masterdata/v2/?limit=' + viewFilter.pageSize + '&offset=' + offset + '&relatedAggregateTypes=Workshop';

    const connectionStatus = viewFilter?.getFilters()?.getFilter('connectionStatus');

    if (connectionStatus) {
      url += '&onlineStatus=' + connectionStatus;
    }

    if (sort) {
      url += '&orderBy=' + sort.active + ':' + sort.direction;
    }

    const textFilter = viewFilter.getFilters().getFilter('text');
    if (!!textFilter && textFilter.length) {
      url += '&searchTerm=' + textFilter;
    }

    return this.serviceCall.get(url).pipe(
      map(resp => {
        return resp.body;
      })
    );
  }

  loadServiceVanDetails(serviceVanId: string): Observable<[ServiceVanMasterDataDto, any[]]> {
    const url: string = environment.apiBaseUrl + '/servicevans/masterdata/v2/' + serviceVanId + '?relatedAggregateTypes=Workshop';
    return this.serviceCall.get(url).pipe(
      map(resp => {
        return [resp?.body?.data, resp?.body?.related];
      })
    );
  }

  /**
   *
   * @param onlineStatus
   * @returns
   */
  private HELPER_getOnlineStatus(onlineStatus: OnlineStatusEnum): 'ONLINE' | 'WAS_ONLINE' | 'OFFLINE' {
    if (!onlineStatus) return 'OFFLINE';
    switch (onlineStatus) {
      case OnlineStatusEnum.ONLINE:
        return 'ONLINE';
      case OnlineStatusEnum.UNKNOWN:
        return 'OFFLINE';
      case OnlineStatusEnum.WAS_ONLINE:
        return 'WAS_ONLINE';
      default:
        return 'OFFLINE';
    }
  }

  getServiceVanMarkerByMasterdata(
    masterdata: ServiceVanMasterDataSto,
    showPopup: boolean,
    silentMarkerUpdate?: boolean
  ): SoffMapMarkerVehicle | null {
    if (!masterdata?.tmGpsPosition || masterdata?.tmGpsPosition?.coordinates?.length !== 2) return null;

    const vehicle: SoffMapModelVehicle = {
      id: masterdata?.aggregateId,
      serial: masterdata?.serial,
      name: masterdata?.name,
      type: 'SERVICEVAN',
    };

    let popupContent: string | null = null;
    if (showPopup) {
      popupContent =
        '<div class="cl-flex-start-start"> \
            <strong>' +
        masterdata?.name +
        '</strong> \
            </div>';
    }

    const marker: SoffMapMarkerVehicle = new SoffMapMarkerVehicle(
      vehicle,
      masterdata?.name,
      'ServiceVans',
      { lat: masterdata?.tmGpsPosition?.coordinates[1], lng: masterdata?.tmGpsPosition?.coordinates[0] },
      0,
      this.HELPER_getOnlineStatus(masterdata?.tmOnlineStatus),
      popupContent,
      null,
      null,
      null,
      silentMarkerUpdate
    );
    return marker;
  }

  /**
   *
   * @param cardInfo
   * @param showPopup
   * @param clickCallback
   * @param mouseInCallback
   * @param mouseOutCallback
   * @returns
   */
  getServiceVanMarkerByCardInfo(
    cardInfo: ServiceVanCardInfoSto,
    showPopup: boolean,
    clickCallback?: (marker: ISoffMapMarker) => void
  ): SoffMapMarkerVehicle | null {
    if (!cardInfo?.latestGpsPosition || cardInfo?.latestGpsPosition?.coordinates?.length !== 2) return null;
    const vehicle: SoffMapModelVehicle = {
      id: cardInfo?.aggregateId,
      serial: cardInfo?.serial,
      name: cardInfo?.name,
      type: 'SERVICEVAN',
    };

    const popupContent: string | null = null;
    if (showPopup) {
      // render popup
    }

    const marker: SoffMapMarkerVehicle = new SoffMapMarkerVehicle(
      vehicle,
      cardInfo?.name,
      'ServiceVans',
      { lat: cardInfo?.latestGpsPosition?.coordinates[1], lng: cardInfo?.latestGpsPosition?.coordinates[0] },
      0,
      this.HELPER_getOnlineStatus(cardInfo?.onlineStatus),
      popupContent,
      clickCallback
    );
    return marker;
  }

  getServiceVanMarkerByTableRow(
    row: ServiceVanListTableRow,
    showPopup: boolean,
    clickCallback?: (marker: ISoffMapMarker) => void
  ): SoffMapMarkerVehicle | null {
    if (!row?.latestGpsPosition || row?.latestGpsPosition?.coordinates?.length !== 2) return null;
    const vehicle: SoffMapModelVehicle = {
      id: row?.aggregateId,
      serial: row?.serial,
      name: row?.name,
      type: 'SERVICEVAN',
    };

    const popupContent: string | null = null;
    if (showPopup) {
      // render popup
    }

    const marker: SoffMapMarkerVehicle = new SoffMapMarkerVehicle(
      vehicle,
      row?.name,
      'ServiceVans',
      { lat: row?.latestGpsPosition?.coordinates[1], lng: row?.latestGpsPosition?.coordinates[0] },
      0,
      this.HELPER_getOnlineStatus(row?.onlineStatus),
      popupContent,
      clickCallback
    );
    return marker;
  }

  isGpsPositionValid(lat: number, lng: number): boolean {
    if (lat === undefined || lat === null) return false;
    if (lng === undefined || lng === null) return false;
    if (lat === 0 && lng === 0) return false;

    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }
}
